import React from 'react';
import { makeStyles } from '@material-ui/core';
import cx from 'classnames';
import { Link as ReactRouterLink } from 'react-router';

import buildParams from 'ha/utils/buildParams';
import Img from 'ha/ui/next/Img';
import Link from 'ha/ui/next/Link';
import { SlideRendererParams } from 'ha/components/next/Slider';
import { VirtualizeSlider } from 'ha/components/next/Slider/VirtualizeSlider';

import Card from 'ha/ui/next/Card';
import Grid from 'ha/ui/next/Grid';
import IconButton from 'ha/ui/next/IconButton';
import Badge from 'ha/ui/next/PaperBadge';

import getFirstAvailableDay from 'ha/utils/exclusionDate/firstAvailableDay';
import LazyComponent from 'ha/components/LazyComponent';
import { useFavoriteListing } from 'ha/modules/FavoriteListing';
import FavoriteButton from 'ha/components/next/FavoriteButton';
import Track from 'ha/components/track/SmartTrack';

import ArrowLeftIcon from 'streamline-icons/arrow-button-left-bold.svg';
import ArrowRightIcon from 'streamline-icons/arrow-button-right-bold.svg';

import { useIntl } from 'ha/i18n';
import { useConfig } from 'ha/helpers/config';

import Price from './Price';
import Info from './Info';
import { ListingAttributes } from './types';

const useStyles = makeStyles(theme => ({
  card: {
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
    height: '100%',
    width: '100%',
  },
  media: {
    position: 'relative',
    width: '100%',
    paddingTop: '66.66%' /* 3:2 Aspect Ratio */,
  },
  absolute: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  shadow: {
    top: '75%',
    background:
      'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(35, 35, 35, 0.51) 59%, rgb(0,0,0) 100%)',
    zIndex: 2,
  },
  content: {
    padding: theme.spacing(1.5),
  },
  interactable: {
    zIndex: 2,
  },
  newBadge: {
    marginLeft: -theme.spacing(1.5),
  },
  arrowButton: {
    color: theme.palette.white.main,
    filter: 'drop-shadow( 0px 2px 2px rgba(0, 0, 0, .7))',
  },
  favoriteButton: {
    marginLeft: 'auto',
  },
  imageCover: {
    objectFit: 'cover',
  },
  fullHeight: {
    height: '100%',
  },
}));

interface Props {
  room: ListingAttributes;
  isLazy?: boolean;
  withGallery?: boolean;
  withFavorites?: boolean;
  noFollow?: boolean;
  onMouseEnter?: (id: number) => void;
  onMouseLeave?: (id: number) => void;
  onCardClick?: () => void;
  queryParams?: {
    startDate: string | null;
    endDate: string | null;
  };
  linkTarget?: '_blank' | undefined;
}

export const ListingCard: React.FC<Props> = ({
  onMouseEnter,
  onMouseLeave,
  room,
  withGallery,
  withFavorites,
  isLazy,
  onCardClick,
  queryParams = {},
  noFollow,
  linkTarget,
}) => {
  const [index, setIndex] = React.useState(0);

  const handlePrevIndex = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      setIndex(index - 1);
    },
    [index],
  );
  const handleNextIndex = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      setIndex(index + 1);
    },
    [index],
  );
  const handleChangeIndex = React.useCallback((newIndex: number) => {
    setIndex(newIndex);
  }, []);

  const {
    onClick: onFavoriteClick,
    isLoading: isFavoriteLoading,
    isFavorite,
  } = useFavoriteListing(room.id);

  const handleMouseEnter = React.useCallback(() => {
    if (onMouseEnter) {
      onMouseEnter(room.id);
    }
  }, [onMouseEnter, room.id]);
  const handleMouseLeave = React.useCallback(() => {
    if (onMouseLeave) {
      onMouseLeave(room.id);
    }
  }, [onMouseLeave, room.id]);

  const { T, urlResolver, formatDate } = useIntl();
  const classes = useStyles();
  const appConfig = useConfig();

  const imageTimeoutMs =
    appConfig && appConfig.thumb && appConfig.thumb.imageTimeoutMs;

  const firstAvailableDay = getFirstAvailableDay(
    room.exclusions,
    room.minimalRentalPeriod,
  );

  const listingDatesLabel = firstAvailableDay
    ? T('Available from %(dates)s', {
        dates: formatDate('DD MMM YYYY', firstAvailableDay),
      })
    : false;

  const isGalleryRendered =
    withGallery && room.photos && room.photos.length > 0;

  const listingPicture = isGalleryRendered ? (
    <VirtualizeSlider
      overscanSlideAfter={1}
      overscanSlideBefore={1}
      slideCount={Math.min(room.photos?.length || 0, 5)}
      style={{ height: '100%' }}
      containerStyle={{ height: '100%' }}
      index={index}
      onChangeIndex={handleChangeIndex}
      slideRenderer={({ index: currentIndex, key }: SlideRendererParams) => {
        const image = room.photos && room.photos[currentIndex];

        if (!image) {
          return null;
        }

        return (
          <Img
            key={key}
            itemProp="image"
            className={cx(classes.imageCover, classes.fullHeight)}
            src={image}
            timeoutMs={imageTimeoutMs}
            isResponsive
            role="presentation"
          />
        );
      }}
    />
  ) : (
    <Img
      itemProp="image"
      src={room.previewImage}
      alt={`${listingDatesLabel} (${room.street}, ${room.city})`}
      timeoutMs={imageTimeoutMs}
      className={cx(classes.imageCover, classes.fullHeight)}
      isResponsive
      role="presentation"
      dataTestLocator="ListingCard No Photo"
    />
  );

  const LinkComponent = linkTarget === '_blank' ? Link : ReactRouterLink;

  return (
    <LinkComponent
      to={`${urlResolver.formatHrefURL(room.listingPath)}${buildParams(
        queryParams,
      )}`}
      target={linkTarget}
      rel={noFollow ? 'noopener noreferrer' : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-test-locator="Listing Card"
      onClick={onCardClick}
      itemScope
      itemType="http://schema.org/Offer"
    >
      <Card elevation={1} className={classes.card}>
        <div className={classes.media}>
          <div
            className={cx(
              classes.absolute,
              classes.fullHeight,
              classes.interactable,
            )}
          >
            {isLazy ? (
              <LazyComponent bottomOffset="-50%">
                {listingPicture}
              </LazyComponent>
            ) : (
              listingPicture
            )}
          </div>
          <Grid
            container
            alignContent="center"
            className={cx(classes.absolute, classes.content)}
          >
            {isGalleryRendered && (
              <Grid item container justify="space-between">
                <IconButton
                  // div because buttons inside links are incorrect according to HTML5 specs
                  component="div"
                  edge="start"
                  aria-label={T('Previous image')}
                  className={cx(classes.arrowButton, classes.interactable)}
                  onClick={handlePrevIndex}
                >
                  <ArrowLeftIcon />
                </IconButton>
                <IconButton
                  component="div"
                  edge="end"
                  aria-label={T('Next image')}
                  className={cx(classes.arrowButton, classes.interactable)}
                  onClick={handleNextIndex}
                >
                  <ArrowRightIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
          <div className={cx(classes.absolute, classes.shadow)} />
          <Grid
            className={cx(classes.absolute, classes.content)}
            container
            direction="column"
            justify="space-between"
          >
            <Grid item container justify="space-between">
              {room.isNew && (
                <Grid
                  item
                  className={cx(classes.newBadge, classes.interactable)}
                >
                  <Badge kind="mint-left-no-radius">{T('New')}</Badge>
                </Grid>
              )}
              {withFavorites && (
                <Grid
                  item
                  className={cx(classes.favoriteButton, classes.interactable)}
                >
                  <Track
                    name="Favorite clicked"
                    type="button"
                    getDynamicData={() => ({
                      listingId: room.id,
                      favoriteType: isFavorite ? 'unfavorite' : 'favorite',
                    })}
                  >
                    <FavoriteButton.Next
                      size="xs"
                      onClick={onFavoriteClick}
                      isLoading={isFavoriteLoading}
                      isFavorite={isFavorite}
                    />
                  </Track>
                </Grid>
              )}
            </Grid>
            <Grid item className={classes.interactable}>
              <Price room={room} />
            </Grid>
          </Grid>
        </div>
        <div className={classes.content}>
          <Info room={room} />
        </div>
      </Card>
    </LinkComponent>
  );
};

export default ListingCard;
